"use client";

import { useCallback, useEffect, useState } from "react";
import { useCheckPermissions } from "./useCheckPermissions";
import { useAccountActionPermissions } from "./useAccountActionPermissions";

const tableKindToAssetType = {
  table: "table",
  virtual_table: "table_view",
} as const;

/**
 * Returns what actions a user can perform on a Table.
 *
 * Currently, all actions are allowed until the Permissions API can be used.
 */
export function useTableActionPermissions(
  tableId: string,
  tableKind: "table" | "virtual_table",
) {
  // a toggleable state to opt in for fetching permissions
  const [permissionsCheckEnabled, setPermissionsCheckEnabled] = useState(false);
  const enablePermissionsCheck = useCallback(() => {
    setPermissionsCheckEnabled(true);
  }, []);

  const { permissions, checkPermissions, isLoading } = useCheckPermissions({
    assetId: tableId,
    assetType: tableKindToAssetType[tableKind],
    permissionLevels: ["can_edit", "can_delete", "can_change_owner"],
  });

  const { allowTeams: hasTeams } = useAccountActionPermissions();

  // Only check permissions if the user has enabled the check
  // and the permissions for the given table have not been fetched yet

  useEffect(() => {
    if (
      tableId &&
      tableKind &&
      permissionsCheckEnabled &&
      !permissions &&
      !isLoading
    ) {
      checkPermissions();
    }
  }, [
    permissionsCheckEnabled,
    isLoading,
    permissions,
    tableId,
    tableKind,
    checkPermissions,
  ]);

  return {
    canDelete: permissions?.canDelete || false,
    canRename: permissions?.canEdit || false,
    canMove: permissions?.canEdit || false,
    canChangeOwner: (permissions?.canChangeOwner && hasTeams) || false,
    canShare: (permissions?.canEdit && hasTeams) || false,
    canDuplicate: true, // anyone with view access can duplicate a table
    enablePermissionsCheck,
    isLoading,
    hasTeams,
  };
}
